import { forwardRef, useMemo, useState } from 'react';

import { RiCalendarLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  DatePickerInput as MantineDatePickerInput,
  DatePickerInputProps
} from '@mantine/dates';
import { dayjs } from '@ui';

import { Badge, Indicator } from '../../datadisplay';
import { useMergeClassNames } from '../../utils';
import dateInputClasses from '../dateinput/DateInput.module.css';

import { DatePickerInputOverrideClassNames } from './DatePickerInput.styles';

// Helper function to convert string dates to Date objects
const parseStringToDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    // Try to parse the string using dayjs
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.toDate() : null;
  }
  return null;
};

// Enhanced props interface for string date support
interface DatePickerInputWithStringProps
  extends Omit<DatePickerInputProps<'default'>, 'value' | 'onChange'> {
  value?: Date | string | null;
  onChange?: (value: Date | null) => void;
  hideDaysCount?: boolean; // Optional prop to hide the days count
}

// restricted to 'default' type for now! if we need to support other types, we can extend this
const DatePickerInput = forwardRef<
  HTMLButtonElement,
  DatePickerInputWithStringProps
>(
  (
    {
      size = 'md',
      type = 'default',
      error,
      leftSection,
      rightSection,
      valueFormat = 'DD/MM/YYYY',
      classNames,
      hideDaysCount = false,
      onFocus,
      onBlur,
      value,
      onChange,
      ...rest
    },
    ref
  ) => {
    const [focussed, setFocussed] = useState<boolean>(false);

    // Parse the incoming value to a Date object for Mantine
    const parsedValue = useMemo(() => parseStringToDate(value), [value]);

    // show elapsed time since the date
    const Elapsed = useMemo(() => {
      if (hideDaysCount || !parsedValue) return null;

      const noOfYearsToDate = dayjs().diff(dayjs(value), 'years');
      const noOfDaysToDate = dayjs().diff(dayjs(value), 'days');
      let elapsedText = null;
      if (noOfYearsToDate > 0) {
        elapsedText = `${noOfYearsToDate} Year${noOfYearsToDate > 1 ? 's' : ''}`;
      } else if (noOfDaysToDate > 0) {
        elapsedText = `${noOfDaysToDate} Day${noOfDaysToDate > 1 ? 's' : ''}`;
      } else if (noOfDaysToDate === 0) {
        elapsedText = 'Today';
      }
      return elapsedText ? (
        <Badge size="sm" color="secondary" variant="light" radius="xs">
          {elapsedText}
        </Badge>
      ) : null;
    }, [value, parsedValue, hideDaysCount]);

    const rightSectionFinal = error ? (
      <RiErrorWarningLine />
    ) : rightSection ? (
      rightSection
    ) : !hideDaysCount ? (
      Elapsed
    ) : null;

    // Handle value changes from the date picker
    const handleDateChange = (newDate: Date | null) => {
      if (onChange) {
        onChange(newDate);
      }
    };

    const classes = useMemo(() => {
      return DatePickerInputOverrideClassNames({
        size,
        focussed,
        error,
        rightSection: rightSectionFinal,
        largeRightSection: rightSectionFinal === Elapsed
      });
    }, [size, focussed, error, rightSectionFinal, Elapsed]);

    const handleFocus = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
      setFocussed(true);
      onFocus && onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
      setFocussed(false);
      onBlur && onBlur(e);
    };

    const mergedClassNames = useMergeClassNames({
      classNames: [classes, classNames]
    });

    const props = {
      ref,
      leftSection: leftSection ? leftSection : <RiCalendarLine />,
      type,
      size,
      error,
      rightSection: rightSectionFinal,
      valueFormat: valueFormat,
      value: parsedValue,
      onChange: handleDateChange,
      renderDay: (date: any) => {
        const isToday = dayjs(date).isToday();
        return (
          <Indicator
            position="bottom-center"
            disabled={!isToday}
            classNames={{
              indicator: dateInputClasses.indicator
            }}
          >
            <div>{date.getDate()}</div>
          </Indicator>
        );
      },
      onFocus: handleFocus,
      onBlur: handleBlur,
      classNames: mergedClassNames,
      editable: true,
      ...rest
    };

    return <MantineDatePickerInput<'default'> {...props} />;
  }
);

export { DatePickerInput };
export type { DatePickerInputWithStringProps as DatePickerInputProps };
